import type { InternalNetworkAttackPathEventDto } from '@terra/shared-contracts-internal-network';
import type { InfraImpact } from '@terra/shared-schemas-orchestrator';
import type {
  AttackPathChainStatus,
  AttackPathEventNodeData,
} from './attack-path-event-node.types';
import { attackPathElapsedSeconds } from './attack-path-timeline-clock.util';

export const PROVENANCE_LABEL: Record<string, string> = {
  DISCOVERED: 'Discovered',
  RELAYED: 'Relayed',
  CRACKED: 'Cracked',
  COMPROMISED: 'Compromised',
};

export const CREDENTIAL_TYPE_LABEL: Record<string, string> = {
  DISCOVERED: 'Discovered Credential',
  RELAYED: 'Relayed Credential',
  CRACKED: 'Cracked Credential',
  COMPROMISED: 'Compromised Credential',
};

export const PRIVILEGE_TIER_LABEL: Record<string, string> = {
  UNKNOWN: '',
  LOCAL_USER: 'Local User',
  LOCAL_ADMIN: 'Local Admin',
  DOMAIN_USER: 'Domain User',
  SERVICE_ACCOUNT: 'Service Account',
  DOMAIN_ADMIN: 'Domain Admin',
  ENTERPRISE_ADMIN: 'Enterprise Admin',
};

export const RESOURCE_KIND_LABEL: Record<string, string> = {
  REPOSITORY: 'Repository',
  FILE_SHARE: 'File Share',
  DATABASE: 'Database',
  OBJECT_STORAGE: 'Object Storage',
  MAILBOX: 'Mailbox',
  BACKUP: 'Backup',
};

export const INFRA_IMPACT_LABEL: Record<InfraImpact, string> = {
  DOMAIN_COMPROMISE: 'Domain Compromise',
  HOST_COMPROMISE: 'Host Compromise',
  DOMAIN_USER_COMPROMISE: 'Domain User Compromise',
  SENSITIVE_DATA_EXPOSURE: 'Sensitive Data Exposure',
  RANSOMWARE_EXPOSURE: 'Ransomware Exposure',
  CREDENTIAL_EXPOSURE: 'Credential Exposure',
  PRIVILEGE_ESCALATION: 'Privilege Escalation',
  LATERAL_MOVEMENT: 'Lateral Movement',
};

const formatPathRelation = ({
  source,
  target,
}: {
  source: string | null;
  target: string | null;
}): string | null => {
  if (source && target && source !== target) {
    return `${source} \u2192 ${target}`;
  }

  return target ?? source ?? null;
};

export const formatAttackPathRetryLabel = (
  retryCount: number,
): string | null => {
  if (retryCount <= 0) {
    return null;
  }

  return retryCount === 1 ? '1 retry' : `${retryCount} retries`;
};

export const resolveAttackPathEventTypeLabel = (
  event: InternalNetworkAttackPathEventDto,
): string => {
  if (event.status === 'FAILED') {
    switch (event.kind) {
      case 'WEAKNESS_EXPLOITED':
        return 'Failed weakness';
      case 'CREDENTIAL_OBTAINED':
        return 'Failed credential';
      case 'DATA_ACCESSED':
        return 'Failed data access';
      case 'IMPACT_ACHIEVED':
        return 'Failed impact';
      case 'HOST_DISCOVERED':
        return 'Failed host';
      case 'SERVICE_DISCOVERED':
        return 'Failed service';
      default:
        return 'Failed';
    }
  }

  switch (event.kind) {
    case 'HOST_DISCOVERED':
      return event.isDomainController
        ? 'Found Domain Controller'
        : 'Found Host';
    case 'SERVICE_DISCOVERED':
      return 'Found Service';
    case 'WEAKNESS_EXPLOITED':
      return event.status === 'IN_PROGRESS'
        ? 'Attempting weakness'
        : 'Exploited Weakness';
    case 'CREDENTIAL_OBTAINED':
      return CREDENTIAL_TYPE_LABEL[event.provenance];
    case 'DATA_ACCESSED':
      return event.resourceKind === 'REPOSITORY'
        ? 'Accessed Repo'
        : 'Accessed Data';
    case 'IMPACT_ACHIEVED':
      return 'Achieved Impact';
    default:
      return 'Event';
  }
};

export const buildAttackPathEventNodeData = ({
  event,
  column,
  lane,
  startMs,
  chainProgress = 0,
  chainStatus = 'IN_PROGRESS' as AttackPathChainStatus,
  campaignStatus = null,
  isCampaignRunning = false,
}: {
  event: InternalNetworkAttackPathEventDto;
  column: number;
  lane: number;
  startMs: number | null;
  chainProgress?: number;
  chainStatus?: AttackPathChainStatus;
  campaignStatus?: AttackPathEventNodeData['campaignStatus'];
  isCampaignRunning?: boolean;
}): AttackPathEventNodeData => {
  const elapsedSeconds = attackPathElapsedSeconds({
    occurredAt: event.occurredAt,
    startMs,
  });
  const base = {
    role: 'EVENT' as const,
    campaignId: null,
    campaignStatus,
    isCampaignRunning,
    chainProgress,
    kind: event.kind,
    typeLabel: resolveAttackPathEventTypeLabel(event),
    caption: null as string | null,
    weaknessId: null as string | null,
    mitreTechniqueId: event.mitreTechniqueId,
    provenance: null,
    privilegeTier: null,
    resourceKind: null,
    severity: null as string | null,
    status: event.status,
    isProven: false,
    exploitsWeakness: event.exploitsWeakness,
    isCrownJewel: false,
    isDomainController: false,
    impacts: [] as InfraImpact[],
    elapsedSeconds,
    occurredAt: event.occurredAt,
    hopIndex: event.hopIndex,
    retryCount: 0,
    findingId: null as string | null,
    hostname: null as string | null,
    entityKind: null,
    entityId: null,
    objective: null,
    signalId: null,
    durationSeconds: null,
    chainStatus,
    column,
    lane,
  } satisfies Partial<AttackPathEventNodeData>;

  switch (event.kind) {
    case 'HOST_DISCOVERED':
      return {
        ...base,
        title: event.hostname,
        subtitle: event.isDomainController ? 'Domain Controller' : 'Host',
        isDomainController: event.isDomainController,
        isCrownJewel: event.isCrownJewel,
        hostname: event.hostname,
        entityId: event.adEntityId,
        caption: event.operatingSystem,
      };
    case 'SERVICE_DISCOVERED':
      return {
        ...base,
        title: event.serviceName,
        subtitle: `${event.protocol.toUpperCase()}/${event.port}`,
        hostname: event.hostname,
        caption: event.product
          ? [event.product, event.version].filter(Boolean).join(' ')
          : null,
      };
    case 'WEAKNESS_EXPLOITED': {
      const retryLabel = formatAttackPathRetryLabel(event.retryCount ?? 0);

      return {
        ...base,
        title: event.title,
        subtitle:
          formatPathRelation({
            source: event.sourceHostname,
            target: event.hostname,
          }) ?? event.weaknessId,
        caption: retryLabel ?? event.weaknessId,
        weaknessId: event.weaknessId,
        severity: event.severity,
        isProven: event.isProven,
        findingId: event.findingId,
        hostname: event.hostname,
        retryCount: event.retryCount ?? 0,
      };
    }

    case 'CREDENTIAL_OBTAINED':
      return {
        ...base,
        title: event.label,
        subtitle: event.username
          ? event.domain
            ? `${event.domain}\\${event.username}`
            : event.username
          : PROVENANCE_LABEL[event.provenance],
        provenance: event.provenance,
        privilegeTier: event.privilegeTier,
        hostname: event.hostname,
        caption: PRIVILEGE_TIER_LABEL[event.privilegeTier],
      };
    case 'DATA_ACCESSED':
      return {
        ...base,
        title: event.resourceName,
        subtitle: RESOURCE_KIND_LABEL[event.resourceKind],
        resourceKind: event.resourceKind,
        hostname: event.hostname,
        caption:
          event.recordCount !== null
            ? `${event.recordCount.toLocaleString()} records`
            : null,
      };
    case 'IMPACT_ACHIEVED':
      return {
        ...base,
        title: INFRA_IMPACT_LABEL[event.infraImpact],
        subtitle:
          formatPathRelation({
            source: event.sourceHostname,
            target: event.hostname,
          }) ?? 'Impact',
        impacts: [event.infraImpact],
        findingId: event.findingId,
        hostname: event.hostname,
      };
    default:
      return { ...base, title: 'Event', subtitle: null };
  }
};
